/*
  Product purchase behaviour.

  <variant-picker>  resolves the chosen options to a variant, keeps the form,
                    the URL and the server-rendered regions in sync, and emits
                    "variant:changed".
  <product-form>    adds to the cart without leaving the page.
  <share-button>    shares or copies the product link.

  The picker and the form live in different blocks, so they find each other
  through the nearest [data-product-section] ancestor. Regions that depend on
  the variant carry data-product-region="<name>" and are swapped for the
  server's version of the same region after each change.
*/
(function () {
  const parseHTML = (html) => new DOMParser().parseFromString(html, 'text/html');

  const sameOptions = (variant, options) => variant.options.every((value, index) => value === options[index]);

  if (!customElements.get('variant-picker')) {
    class VariantPicker extends HTMLElement {
      connectedCallback() {
        if (this.ready) return;
        this.ready = true;

        const data = this.querySelector('[data-variants]');
        try {
          this.variants = data ? JSON.parse(data.textContent) : [];
        } catch (error) {
          this.variants = [];
        }
        /* Liquid lists a limited number of variants; larger products fall back to the server */
        this.incomplete = Number(this.dataset.variantsTotal) > this.variants.length;

        this.addEventListener('change', (event) => this.onChange(event));
      }

      get root() {
        return this.closest('[data-product-section]') || this.parentElement;
      }

      get optionGroups() {
        return Array.from(this.querySelectorAll('[data-option]'));
      }

      selectedOptions() {
        return this.optionGroups.map((group) => {
          const select = group.querySelector('select');
          if (select) return select.value;
          const checked = group.querySelector('input:checked');
          return checked ? checked.value : null;
        });
      }

      selectedValueIds() {
        return this.optionGroups
          .map((group) => {
            const select = group.querySelector('select');
            const chosen = select ? select.selectedOptions[0] : group.querySelector('input:checked');
            return chosen ? chosen.dataset.valueId : null;
          })
          .filter(Boolean);
      }

      async onChange(event) {
        let options = this.selectedOptions();
        let variant = this.variants.find((candidate) => sameOptions(candidate, options)) || null;

        /* Many catalogues list only some combinations (a wheel size exists with one offset, not all
           of them). Rather than showing "Unavailable", move to the closest variant that keeps the
           value the shopper just picked. */
        if (!variant && !this.incomplete && event) {
          const changed = this.optionGroups.findIndex((group) => group.contains(event.target));
          const closest = this.closestVariant(options, changed);
          if (closest) {
            variant = closest;
            options = closest.options.slice();
            this.setSelected(options);
          }
        }

        this.optionGroups.forEach((group, index) => {
          const label = group.querySelector('[data-selected-value]');
          if (label) label.textContent = options[index];
        });

        const ticket = (this.ticket = (this.ticket || 0) + 1);
        let page = null;

        if (!variant && this.incomplete) {
          page = await this.lookUpOnServer(options);
          if (ticket !== this.ticket) return;
          variant = page ? page.variant : null;
          page = page ? page.document : null;
        } else if (!this.incomplete) {
          /* With a partial variant list the server's picker is the only reliable source; see syncAvailability */
          this.markAvailability(options);
        }

        this.apply(variant, page);
      }

      closestVariant(options, changedIndex) {
        if (changedIndex < 0) return null;
        let best = null;
        let bestScore = -1;
        this.variants.forEach((candidate) => {
          if (candidate.options[changedIndex] !== options[changedIndex]) return;
          let score = candidate.options.reduce((total, value, index) => total + (value === options[index] ? 2 : 0), 0);
          if (candidate.available) score += 1;
          if (score > bestScore) {
            best = candidate;
            bestScore = score;
          }
        });
        return best;
      }

      setSelected(options) {
        this.optionGroups.forEach((group, index) => {
          const select = group.querySelector('select');
          if (select) {
            select.value = options[index];
            return;
          }
          group.querySelectorAll('input[type="radio"]').forEach((input) => {
            input.checked = input.value === options[index];
          });
        });
      }

      /* Flags every value that has no purchasable variant alongside the other chosen values. */
      markAvailability(options) {
        this.optionGroups.forEach((group, index) => {
          const isAvailable = (value) => {
            const wanted = options.slice();
            wanted[index] = value;
            return this.variants.some((variant) => variant.available && sameOptions(variant, wanted));
          };
          group.querySelectorAll('input[type="radio"]').forEach((input) => {
            this.flagInput(input, !isAvailable(input.value));
          });
          group.querySelectorAll('option').forEach((option) => {
            this.flagOption(option, !isAvailable(option.value));
          });
        });
      }

      flagInput(input, unavailable) {
        const label = input.labels && input.labels[0];
        if (!label) return;
        label.classList.toggle('is-unavailable', unavailable);
        const note = label.querySelector('[data-unavailable-label]');
        if (note) note.hidden = !unavailable;
      }

      flagOption(option, unavailable) {
        const name = option.dataset.label || option.value;
        option.textContent = unavailable ? `${name} - ${this.dataset.unavailableText}` : name;
      }

      /* For products with more variants than the embedded JSON holds. */
      async lookUpOnServer(options) {
        const url = new URL(this.dataset.productUrl, window.location.origin);
        url.searchParams.set('option_values', this.selectedValueIds().join(','));
        try {
          const page = parseHTML(await Theme.fetchSection(url.toString(), this.dataset.sectionId));
          const data = page.querySelector('variant-picker [data-selected-variant]');
          const variant = data ? JSON.parse(data.textContent) : null;

          if (!variant || !sameOptions(variant, options)) return null;
          this.syncAvailability(page);
          return { variant, document: page };
        } catch (error) {
          return null;
        }
      }

      /* Copies the unavailable flags from a server-rendered picker onto this one. */
      syncAvailability(page) {
        page.querySelectorAll('variant-picker input[type="radio"]').forEach((fresh) => {
          const input = fresh.id ? document.getElementById(fresh.id) : null;
          const label = page.querySelector(`label[for="${fresh.id}"]`);
          if (input && label && this.contains(input)) {
            this.flagInput(input, label.classList.contains('is-unavailable'));
          }
        });
        page.querySelectorAll('variant-picker select').forEach((fresh) => {
          const select = fresh.id ? document.getElementById(fresh.id) : null;
          if (!select || !this.contains(select)) return;
          Array.from(fresh.options).forEach((option, index) => {
            if (select.options[index]) select.options[index].textContent = option.textContent.trim();
          });
        });
      }

      apply(variant, page) {
        const root = this.root;
        const form = document.getElementById(this.dataset.formId);
        const idInput = form ? form.querySelector('[data-variant-id-input]') : null;
        if (idInput) idInput.value = variant ? variant.id : '';

        if (variant && this.hasAttribute('data-update-url')) {
          const url = new URL(window.location.href);
          url.searchParams.set('variant', variant.id);
          window.history.replaceState(window.history.state, '', url.toString());
        }

        Theme.emit('variant:changed', {
          variant,
          productId: Number(this.dataset.productId),
          sectionId: this.dataset.sectionId,
        });

        if (this.request) this.request.abort();

        if (!variant) {
          this.setAddButton(root, false, Theme.strings.unavailable);
          root.querySelectorAll('[data-product-region]').forEach((region) => {
            if (['inventory', 'sku', 'price'].includes(region.dataset.productRegion)) region.hidden = true;
          });
          return;
        }

        this.renderRegions(root, variant, page);
      }

      async renderRegions(root, variant, page) {
        if (!root.querySelector('[data-product-region]')) return;

        const matches = (candidate) =>
          candidate && candidate.querySelector(`[data-product-region][data-variant-id="${variant.id}"]`);

        this.request = new AbortController();
        const { signal } = this.request;

        try {
          let fresh = matches(page) ? page : null;

          /* The product URL is tried first; a product shown on another page is re-rendered from that page. */
          const sources = new Set([
            new URL(this.dataset.productUrl, window.location.origin).pathname,
            window.location.pathname,
          ]);
          for (const source of sources) {
            if (fresh) break;
            const url = new URL(source, window.location.origin);
            url.searchParams.set('variant', variant.id);
            try {
              const candidate = parseHTML(await Theme.fetchSection(url.toString(), this.dataset.sectionId, { signal }));
              if (matches(candidate)) fresh = candidate;
            } catch (error) {
              if (error.name === 'AbortError') throw error;
            }
          }

          if (fresh) {
            this.swapRegions(root, fresh);
            if (this.incomplete) this.syncAvailability(fresh);
          } else {
            this.renderWithoutServer(root, variant);
          }
        } catch (error) {
          if (error.name !== 'AbortError') this.renderWithoutServer(root, variant);
        }

        const payment = root.querySelector('[data-payment-button]');
        if (payment && !signal.aborted) payment.hidden = !variant.available;
      }

      swapRegions(root, fresh) {
        root.querySelectorAll('[data-product-region]').forEach((region) => {
          const name = region.dataset.productRegion;
          const replacement = fresh.querySelector(`[data-product-region="${name}"]`);
          if (!replacement) return;

          const hadFocus = region === document.activeElement;
          const quantity = name === 'quantity' ? region.querySelector('input[type="number"]') : null;
          const plan = name === 'selling-plans' ? region.querySelector('input:checked') : null;

          region.replaceWith(replacement);

          if (quantity) {
            const input = replacement.querySelector('input[type="number"]');
            if (input && Number(quantity.value) > Number(input.value)) {
              input.value = quantity.value;
              input.dispatchEvent(new Event('change', { bubbles: true }));
            }
          }
          if (plan) {
            const same = Array.from(replacement.querySelectorAll('input[type="radio"]')).find(
              (input) => input.value === plan.value
            );
            if (same) same.checked = true;
          }
          if (hadFocus && !replacement.disabled) replacement.focus();
        });
      }

      /* Last resort when the server cannot be reached: keep the essentials truthful. */
      renderWithoutServer(root, variant) {
        this.setAddButton(root, variant.available, variant.available ? Theme.strings.addToCart : Theme.strings.soldOut);

        root.querySelectorAll('[data-product-region]').forEach((region) => {
          const name = region.dataset.productRegion;
          if (name === 'inventory' || name === 'sku') region.hidden = true;
          if (name === 'price') {
            const price = region.querySelector('.price');
            const current = region.querySelector('.price__current');
            const compare = region.querySelector('.price__compare');
            const unit = region.querySelector('.price__unit');
            const struck = compare ? compare.querySelector('s') : null;
            const onSale = Boolean(struck) && Number(variant.compare_at_price) > Number(variant.price);
            if (current) current.textContent = Theme.formatMoney(variant.price);
            if (onSale) struck.textContent = Theme.formatMoney(variant.compare_at_price);
            if (compare) compare.hidden = !onSale;
            if (price) price.classList.toggle('price--on-sale', onSale);
            /* the unit price cannot be rebuilt reliably here, so it is not shown rather than left stale */
            if (unit) unit.hidden = true;
            const termsId = region.querySelector('form input[name="id"]');
            if (termsId) termsId.value = variant.id;
            region.hidden = false;
          }
        });
      }

      setAddButton(root, enabled, text) {
        const button = root.querySelector('[data-product-region="add-button"]');
        if (!button) return;
        button.disabled = !enabled;
        const label = button.querySelector('[data-add-label]');
        if (label && text) label.textContent = text;
        const payment = root.querySelector('[data-payment-button]');
        if (payment) payment.hidden = !enabled;
      }
    }

    customElements.define('variant-picker', VariantPicker);
  }

  if (!customElements.get('product-form')) {
    class ProductForm extends HTMLElement {
      connectedCallback() {
        this.stopListening = Theme.on('cart:updated', () => this.refreshQuantity());
        if (this.ready) return;
        this.ready = true;

        this.form = this.querySelector('form');
        if (!this.form) return;
        this.errorBox = this.querySelector('[data-form-error]');
        this.status = this.querySelector('[data-form-status]');

        this.form.addEventListener('submit', (event) => this.onSubmit(event));
        this.setUpRecipientFields();
      }

      disconnectedCallback() {
        if (this.stopListening) this.stopListening();
      }

      /*
        Quantity rules depend on how many of the variant are already in the cart,
        so the quantity block is rendered again whenever the cart changes.
      */
      async refreshQuantity() {
        const root = this.closest('[data-product-section]');
        const idInput = this.form ? this.form.querySelector('[data-variant-id-input]') : null;
        const id = idInput ? idInput.value : '';
        if (!root || !id || !this.dataset.productUrl) return;
        if (!root.querySelector('[data-product-region="quantity"][data-cart-aware]')) return;

        try {
          const url = new URL(this.dataset.productUrl, window.location.origin);
          url.searchParams.set('variant', id);
          const page = parseHTML(await Theme.fetchSection(url.toString(), this.dataset.sectionId));
          const fresh = page.querySelector(`[data-product-region="quantity"][data-variant-id="${id}"]`);
          const current = root.querySelector('[data-product-region="quantity"]');
          if (fresh && current && current.dataset.variantId === id) current.replaceWith(fresh);
        } catch (error) {
          /* the cart still enforces the rule and reports it when the form is submitted */
        }
      }

      get addButton() {
        return this.form.querySelector('[data-product-region="add-button"]');
      }

      /* Gift card recipient fields are only sent when the shopper opts in. */
      setUpRecipientFields() {
        const toggle = this.form.querySelector('[data-recipient-toggle]');
        const fields = this.form.querySelector('[data-recipient-fields]');
        if (!toggle || !fields) return;

        const offset = fields.querySelector('[data-recipient-offset]');
        if (offset) offset.value = new Date().getTimezoneOffset();

        const sync = () => {
          fields.hidden = !toggle.checked;
          fields.querySelectorAll('input, textarea').forEach((field) => {
            field.disabled = !toggle.checked;
            if (field.hasAttribute('data-recipient-required')) field.required = toggle.checked;
          });
        };
        toggle.addEventListener('change', sync);
        sync();
      }

      async onSubmit(event) {
        const button = this.addButton;
        if (!button) return;
        /* Other submitters, such as checkout buttons, keep their own behaviour */
        if (event.submitter && event.submitter !== button) return;

        event.preventDefault();
        if (this.busy || button.disabled) return;

        this.busy = true;
        button.classList.add('is-loading');
        button.setAttribute('aria-disabled', 'true');
        this.showError('');
        if (this.status) this.status.textContent = '';

        try {
          const response = await fetch(Theme.routes.cartAdd, {
            method: 'POST',
            headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
            body: new FormData(this.form),
          });
          const result = await response.json();

          if (!response.ok || result.status) {
            this.showError(this.describe(result));
            return;
          }

          const cartResponse = await fetch(`${Theme.routes.cart}.js`, { headers: { Accept: 'application/json' } });
          const cart = await cartResponse.json();
          Theme.emit('cart:updated', { cart, source: 'product-form' });

          if (Theme.settings.cartType === 'drawer') {
            if (this.status) this.status.textContent = this.status.dataset.addedText || '';
            Theme.emit('cart:open');
          } else {
            window.location.assign(Theme.routes.cart);
          }
        } catch (error) {
          this.showError(Theme.strings.cartError);
        } finally {
          this.busy = false;
          const current = this.addButton || button;
          current.classList.remove('is-loading');
          current.removeAttribute('aria-disabled');
          button.classList.remove('is-loading');
        }
      }

      /* Cart errors arrive as a string or as a map of field names to messages. */
      describe(result) {
        const flatten = (value) => {
          if (!value) return '';
          if (typeof value === 'string') return value;
          if (Array.isArray(value)) return value.map(flatten).filter(Boolean).join(' ');
          if (typeof value === 'object') return Object.values(value).map(flatten).filter(Boolean).join(' ');
          return '';
        };
        return flatten(result.errors) || flatten(result.description) || flatten(result.message) || Theme.strings.cartError;
      }

      showError(message) {
        if (!this.errorBox) return;
        this.errorBox.textContent = message;
        this.errorBox.hidden = !message;
      }
    }

    customElements.define('product-form', ProductForm);
  }

  if (!customElements.get('share-button')) {
    class ShareButton extends HTMLElement {
      connectedCallback() {
        if (this.ready) return;
        this.ready = true;
        this.button = this.querySelector('button');
        this.status = this.querySelector('[data-share-status]');
        if (this.button) this.button.addEventListener('click', () => this.share());
      }

      async share() {
        const url = this.dataset.url || window.location.href;
        try {
          if (navigator.share) {
            await navigator.share({ url, title: this.dataset.title || document.title });
            return;
          }
          await navigator.clipboard.writeText(url);
          this.announce(this.dataset.copiedText);
        } catch (error) {
          /* the shopper dismissed the share sheet, or clipboard access was refused */
          if (error.name !== 'AbortError') this.announce(url);
        }
      }

      announce(message) {
        if (!this.status) return;
        this.status.textContent = message || '';
        clearTimeout(this.timer);
        this.timer = setTimeout(() => {
          this.status.textContent = '';
        }, 4000);
      }
    }

    customElements.define('share-button', ShareButton);
  }
})();
